import { useMemo, type ReactNode } from "react";
import {
    buildRowGroups,
    type PickerItemMatcher,
    type PickerRowGroup,
} from "./PickerTree";
import type {
    PickerEmptyBehavior,
    PickerItemData,
    PickerItemValue,
    PickerNested,
    PickerSectionData,
    PickerStatus,
} from "./Picker.types";

export interface UsePickerListRowsOptions<T extends PickerItemValue> {
    items?: ReadonlyArray<PickerItemData<T>>;
    sections?: ReadonlyArray<PickerSectionData<T>>;
    expansion: ReadonlyMap<T, boolean>;
    /** Parents absent from `expansion` start expanded. */
    isExpandedByDefault: boolean;
    /** Parents absent from `expansion` that start expanded one by one. */
    defaultExpandedValues?: readonly T[];
    /** The query that filters rows; empty when the text is a selected label. */
    query: string;
    /** The raw text handed to `renderFooter`. */
    rawQuery: string;
    selectedValues: readonly T[];
    selectableParents: boolean;
    nested: PickerNested;
    filter?: PickerItemMatcher<T>;
    renderFooter?: (query: string) => ReactNode;
    isDataDriven: boolean;
    status: PickerStatus;
    /** A paged list with more to load keeps the popup open, so its end marker can ask for the next page. */
    hasMore: boolean;
    loadMoreStatus: PickerStatus;
    emptyBehavior: PickerEmptyBehavior;
    hasPopupSearch: boolean;
}

/**
 * The rows a list root renders: flattened groups from `items` / `sections`
 * under the current expansion and query, the creatable footer, and whether
 * `emptyBehavior="hide"` should close the popup for lack of content.
 */
export const usePickerListRows = <T extends PickerItemValue>({
    items,
    sections,
    expansion,
    isExpandedByDefault,
    defaultExpandedValues,
    query,
    rawQuery,
    selectedValues,
    selectableParents,
    nested,
    filter,
    renderFooter,
    isDataDriven,
    status,
    hasMore,
    loadMoreStatus,
    emptyBehavior,
    hasPopupSearch,
}: UsePickerListRowsOptions<T>): {
    groups: Array<PickerRowGroup<T>>;
    footer: ReactNode;
    isPopupHidden: boolean;
} => {
    const groups = useMemo(() => {
        const expandedValues = new Set<T>();
        const collapsedValues = new Set<T>();
        expansion.forEach((isExpanded, itemValue) => {
            (isExpanded ? expandedValues : collapsedValues).add(itemValue);
        });
        return buildRowGroups({
            items,
            sections,
            expandedValues,
            collapsedValues,
            isExpandedByDefault,
            defaultExpandedValues: new Set(defaultExpandedValues),
            query,
            selectedValues,
            selectableParents,
            nested,
            isMatch: filter,
        });
    }, [
        expansion,
        isExpandedByDefault,
        defaultExpandedValues,
        items,
        sections,
        query,
        selectedValues,
        selectableParents,
        nested,
        filter,
    ]);
    const rowCount = groups.reduce(
        (count, group) => count + group.rows.length,
        0,
    );
    const footer = renderFooter?.(rawQuery);
    const hasFooter = footer !== undefined && footer !== null;
    // `hide` closes the popup only where the root knows its rows and the
    // query lives outside the popup; a popup search must stay reachable, so
    // there only the empty row is dropped. The user's open request survives
    // the close, so the popup returns as soon as a row matches again.
    const hasContent =
        !isDataDriven ||
        rowCount > 0 ||
        hasFooter ||
        status !== "idle" ||
        hasMore ||
        loadMoreStatus !== "idle";
    const isPopupHidden =
        emptyBehavior === "hide" && !hasPopupSearch && !hasContent;

    return { groups, footer, isPopupHidden };
};
